import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Conta uma visita por página, no servidor do próprio site.
 *
 * Nenhum identificador sai daqui: manda o caminho e, só na primeira página
 * depois de carregar o site, de onde a pessoa veio. Não grava nada no
 * navegador, não carrega script de terceiro e não tem o que pedir num
 * banner de consentimento — por isso roda para todo mundo, sem perguntar.
 *
 * Falha em silêncio de propósito: um contador quebrado não pode atrapalhar
 * quem está lendo. Se o envio não sair, o número fica menor e pronto.
 */

/**
 * Vira false depois do primeiro envio desta carga de página.
 *
 * Sem isso, quem chega do Instagram e lê cinco páginas contaria como cinco
 * vindas do Instagram, e o KPI de cliques no link da bio ficaria inflado
 * pela curiosidade de quem já entrou. O módulo é recarregado a cada carga
 * de página de verdade, que é exatamente o escopo que a gente quer — e a
 * navegação do React, que não recarrega, não reconta.
 *
 * Também não dá para usar document.referrer para isso: ele é fixado quando
 * o documento carrega e não muda nas trocas de rota do React, então ele
 * continuaria dizendo "Instagram" na quinta página.
 */
let entradaRegistrada = false;

/** Só o nome do host de quem indicou; a URL inteira não é da nossa conta. */
function origemDaEntrada(): { fonte: string; campanha: string } {
  let fonte = "";
  let campanha = "";

  try {
    const q = new URLSearchParams(window.location.search);
    fonte = q.get("utm_source") ?? "";
    campanha = q.get("utm_campaign") ?? "";
  } catch {
    // query estranha: segue sem utm
  }

  if (!fonte) {
    try {
      const ref = document.referrer;
      if (ref) {
        const host = new URL(ref).hostname.replace(/^www\./, "");
        if (host && host !== window.location.hostname) fonte = host;
      }
    } catch {
      // referrer que não é URL: segue como direto
    }
  }

  return { fonte, campanha };
}

export function useContarVisita(): void {
  const { pathname } = useLocation();

  useEffect(() => {
    // Quem pediu menos rastreamento no navegador é atendido: o sinal existe
    // para isso, mesmo que esta contagem não identifique ninguém.
    if (navigator.doNotTrack === "1") return;

    const dados: { rota: string; fonte?: string; campanha?: string } = {
      rota: pathname,
    };
    if (!entradaRegistrada) {
      entradaRegistrada = true;
      const { fonte, campanha } = origemDaEntrada();
      dados.fonte = fonte;
      dados.campanha = campanha;
    }
    const corpo = JSON.stringify(dados);

    // sendBeacon sobrevive à navegação: sem ele, sair da página rápido
    // demais cancela a requisição e a visita some justamente nas páginas
    // em que a pessoa achou o que queria e foi embora.
    try {
      if (navigator.sendBeacon) {
        navigator.sendBeacon(
          "/api/visita",
          new Blob([corpo], { type: "application/json" }),
        );
        return;
      }
    } catch {
      // cai no fetch abaixo
    }

    void fetch("/api/visita", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: corpo,
      keepalive: true,
    }).catch(() => {});
  }, [pathname]);
}
